import { useState, useEffect, useRef, useCallback } from 'react';
import { LocalLinter, binary, Dialect, Lint } from 'harper.js';

interface GrammarIssue {
  lint: Lint;
  start: number;
  end: number;
  message: string;
  suggestions: string[];
}

interface UseGrammarCheckerOptions {
  enabled?: boolean;
  debounceMs?: number;
  dialect?: Dialect;
}

export function useGrammarChecker(text: string, options: UseGrammarCheckerOptions = {}) {
  const { enabled = true, debounceMs = 500, dialect = Dialect.American } = options;
  
  const [issues, setIssues] = useState<GrammarIssue[]>([]);
  const [isChecking, setIsChecking] = useState(false);
  const [linter, setLinter] = useState<LocalLinter | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Initialize linter
  useEffect(() => {
    let mounted = true;
    
    async function initLinter() {
      try {
        const newLinter = new LocalLinter({
          binary: binary,
          dialect: dialect,
        });
        await newLinter.setup();
        if (mounted) {
          setLinter(newLinter);
          setIsInitialized(true);
        }
      } catch (error) {
        console.error('Failed to initialize grammar checker:', error);
        if (mounted) {
          setIsInitialized(false);
        }
      }
    }

    if (enabled) {
      initLinter();
    }

    return () => {
      mounted = false;
      // Note: LocalLinter cleanup is handled automatically by JavaScript GC
    };
  }, [enabled, dialect]);

  // Check grammar with debouncing
  const checkGrammar = useCallback(async (textToCheck: string) => {
    if (!enabled || !linter || !isInitialized || !textToCheck.trim()) {
      setIssues([]);
      return;
    }

    setIsChecking(true);
    
    try {
      const lints = await linter.lint(textToCheck, { language: 'plaintext' });
      
      const grammarIssues: GrammarIssue[] = lints.map((lint) => {
        const span = lint.span();
        const suggestions = lint.suggestions().map((s) => {
          // Get the suggestion replacement text
          try {
            return s.get_replacement_text() || '';
          } catch (e) {
            return '';
          }
        }).filter(Boolean);

        return {
          lint,
          start: span.start,
          end: span.end,
          message: lint.message(),
          suggestions,
        };
      });

      setIssues(grammarIssues);
    } catch (error) {
      console.error('Grammar check error:', error);
      setIssues([]);
    } finally {
      setIsChecking(false);
    }
  }, [enabled, linter, isInitialized]);

  // Debounced grammar checking
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    if (!enabled || !text) {
      setIssues([]);
      return;
    }

    timeoutRef.current = setTimeout(() => {
      checkGrammar(text);
    }, debounceMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [text, checkGrammar, debounceMs, enabled]);

  // Apply suggestion helper
  const applySuggestion = useCallback(async (issue: GrammarIssue, suggestionIndex: number = 0) => {
    if (!linter || suggestionIndex >= issue.suggestions.length) {
      return text;
    }

    try {
      const suggestion = issue.lint.suggestions()[suggestionIndex];
      if (suggestion) {
        const newText = await linter.applySuggestion(text, issue.lint, suggestion);
        return newText;
      }
    } catch (error) {
      console.error('Failed to apply suggestion:', error);
    }

    return text;
  }, [linter, text]);

  return {
    issues,
    isChecking,
    isInitialized,
    applySuggestion,
    checkGrammar,
  };
}

